import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import {
  Alert,
  Button,
  Card,
  Dialog,
  DialogActions,
  FormControlLabel,
  Link,
  Switch,
  Typography,
} from '@/ui';
import {
  COMPANY_PROVISIONING_PRESENTATION,
  getCompanyProvisioningStatus,
} from '@/utils';

import type { Role, User } from '@/types';
import type { FC } from 'react';

import { coachMatchingReleaseV2Enabled } from '@/features/coach-matching/releaseV2Flag';
import {
  useAddUserRoleMutation,
  useGetUserRolesQuery,
  useRemoveUserRoleMutation,
  useUpdateUserB2BEnabledMutation,
  useUpdateUserMutation,
} from '@/store/api/userApi';

const getB2BEnabledValue = (user: User): boolean =>
  !!(user.b2bEnabled ?? user.b2b_enabled);

const getB2BErrorMessage = (error: unknown): string => {
  const status =
    typeof error === 'object' && error && 'status' in error
      ? (error as { status?: number }).status
      : undefined;

  if (status === 403) {
    return "Only admins can update this member's B2B rollout flag.";
  }

  if (status === 400) {
    return 'The server rejected this B2B rollout change. Refresh and try again.';
  }

  return 'Could not update B2B rollout. Please try again.';
};

const AdminOnly: FC<{
  client: User;
}> = ({ client }) => {
  const navigate = useNavigate();
  const [updateUser] = useUpdateUserMutation();
  const [updateUserB2BEnabled, { isLoading: isUpdatingB2BEnabled }] =
    useUpdateUserB2BEnabledMutation();

  const [processing, setProcessing] = useState(false);
  const [showDeactivate, setShowDeactivate] = useState(false);

  const {
    data: roles = [],
    isLoading: isRolesLoading,
    isError: isRolesError,
    refetch: refetchRoles,
  } = useGetUserRolesQuery(client.id);

  const [addUserRole, { isLoading: isAddingRole }] = useAddUserRoleMutation();
  const [removeUserRole, { isLoading: isRemovingRole }] =
    useRemoveUserRoleMutation();

  const [rolesError, setRolesError] = useState<string | null>(null);
  const [selectedRoleName, setSelectedRoleName] = useState<'support' | 'admin'>(
    'support',
  );
  const [roleToRemove, setRoleToRemove] = useState<Role | null>(null);
  const [showAdminConfirm, setShowAdminConfirm] = useState(false);
  const [b2bRolloutEnabled, setB2bRolloutEnabled] = useState(
    getB2BEnabledValue(client),
  );
  const [b2bRolloutError, setB2bRolloutError] = useState<string | null>(null);
  const currentMemberIdRef = useRef(client.id);
  const b2bRequestIdRef = useRef(0);
  const activeB2BRequestRef = useRef<{
    memberId: string;
    requestId: number;
  } | null>(null);

  useEffect(() => {
    currentMemberIdRef.current = client.id;
    b2bRequestIdRef.current += 1;
    activeB2BRequestRef.current = null;
    setB2bRolloutEnabled(getB2BEnabledValue(client));
    setB2bRolloutError(null);
  }, [client.id, client.b2bEnabled, client.b2b_enabled]);

  const onDeactivate = async () => {
    try {
      setProcessing(true);
      await updateUser({ id: client.id, user: { isActive: false } }).unwrap();
      setProcessing(false);
      setShowDeactivate(false);
      window.location.reload();
    } catch (e) {
      setProcessing(false);
      console.error(e);
    }
  };

  const onActivate = async () => {
    try {
      setProcessing(true);
      await updateUser({ id: client.id, user: { isActive: true } }).unwrap();
      setProcessing(false);
      setShowDeactivate(false);
      window.location.reload();
    } catch (e) {
      setProcessing(false);
      console.error(e);
    }
  };

  const formatRoleLabel = (role: Role): string =>
    // Don't show global/nonglobal scope in the UI
    role.name;
  const canEditRole = (role: Role): boolean =>
    role.name === 'support' ||
    role.name === 'company_admin' ||
    role.name === 'admin';

  // Filter roles to show in the UI
  // Don't expose coach, coachee, or company_admin roles
  // Do expose admin and support
  const shouldShowRole = (role: Role): boolean => {
    const hiddenRoles = ['coach', 'coachee', 'company_admin'];
    return !hiddenRoles.includes(role.name);
  };

  const visibleRoles = roles.filter(shouldShowRole);

  const performAddRole = async () => {
    setRolesError(null);
    const scope = 'global'; // Support and admin are always global

    try {
      await addUserRole({
        companyId: undefined,
        name: selectedRoleName,
        scope,
        userId: client.id,
      }).unwrap();

      setShowAdminConfirm(false);
      await refetchRoles();
    } catch (error) {
      console.error(error);
      setRolesError('Unable to add role. Please try again.');
      setShowAdminConfirm(false);
    }
  };

  const onAddRole = async () => {
    // Show double confirmation for admin role
    if (selectedRoleName === 'admin') {
      setShowAdminConfirm(true);
      return;
    }

    await performAddRole();
  };

  const onConfirmRemoveRole = async () => {
    if (!roleToRemove) return;

    setRolesError(null);

    const scope =
      roleToRemove.resourceType === 'Company' && roleToRemove.resourceId
        ? 'company'
        : 'global';

    try {
      await removeUserRole({
        companyId:
          scope === 'company'
            ? (roleToRemove.resourceId ?? undefined)
            : undefined,
        name: roleToRemove.name,
        scope,
        userId: client.id,
      }).unwrap();

      setRoleToRemove(null);
      await refetchRoles();
    } catch (error) {
      console.error(error);
      setRoleToRemove(null);
      setRolesError('Unable to remove role. Please try again.');
    }
  };

  const onCancelRemoveRole = () => {
    setRoleToRemove(null);
  };

  const company = client.currentCompany;
  const companyId = company?.id;
  const companyProvisioningStatus = company
    ? getCompanyProvisioningStatus(company)
    : undefined;
  const companyNeedsB2BLink = company
    ? !!company.id && companyProvisioningStatus !== 'linked'
    : false;
  const companyProvisioningPresentation = companyProvisioningStatus
    ? COMPANY_PROVISIONING_PRESENTATION[companyProvisioningStatus]
    : undefined;
  const companyAdminPath = companyId
    ? `/coach/manage/companies/${companyId}`
    : '';
  const disableB2BToggle =
    isUpdatingB2BEnabled || (companyNeedsB2BLink && !b2bRolloutEnabled);

  const onB2BToggle = async (nextEnabled: boolean) => {
    const memberId = client.id;
    if (activeB2BRequestRef.current?.memberId === memberId) return;

    b2bRequestIdRef.current += 1;
    const requestId = b2bRequestIdRef.current;
    activeB2BRequestRef.current = { memberId, requestId };
    const isStaleRequest = () =>
      currentMemberIdRef.current !== memberId ||
      activeB2BRequestRef.current?.memberId !== memberId ||
      activeB2BRequestRef.current.requestId !== requestId;

    setB2bRolloutError(null);
    setB2bRolloutEnabled(nextEnabled);

    try {
      await updateUserB2BEnabled({
        enabled: nextEnabled,
        id: memberId,
      }).unwrap();
    } catch (error) {
      if (isStaleRequest()) {
        return;
      }

      setB2bRolloutEnabled(!nextEnabled);
      setB2bRolloutError(getB2BErrorMessage(error));
    } finally {
      if (!isStaleRequest()) activeB2BRequestRef.current = null;
    }
  };

  return (
    <Card className="overflow-hidden bg-white">
      <div
        className={clsx(
          'flex h-6 items-center justify-center bg-red-200 p-4 text-xs',
        )}
      >
        Admin only
      </div>

      <div className="space-y-4 p-6">
        {client.isActive && (
          <Button
            className="w-full"
            color="error"
            variant="contained"
            onClick={() => setShowDeactivate(true)}
          >
            Deactivate Member
          </Button>
        )}
        {!client.isActive && (
          <Button className="w-full" variant="contained" onClick={onActivate}>
            Reactivate Member
          </Button>
        )}
        <Button
          className="w-full"
          variant="outlined"
          onClick={() => navigate(`/coach/members/${client.id}/coaches`)}
        >
          Update Coaches
        </Button>
        {coachMatchingReleaseV2Enabled() && (
          <Button
            className="w-full"
            variant="outlined"
            onClick={() => navigate(`/coach/members/${client.id}/matching`)}
          >
            Find coach matches
          </Button>
        )}

        <div className="mt-2 border-t border-cement-300 pt-4">
          <Typography className="mb-2 text-xs font-semibold uppercase text-gray-600">
            B2B Rollout
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={b2bRolloutEnabled}
                color="primary"
                disabled={disableB2BToggle}
                onChange={(event) => onB2BToggle(event.target.checked)}
              />
            }
            label="Enable B2B rollout for this member"
          />
          {companyNeedsB2BLink && companyProvisioningPresentation && (
            <Alert
              className="mt-3"
              severity={companyProvisioningPresentation.severity}
            >
              {company?.b2bProvisioningMessage && (
                <Typography variant="body2">
                  {company.b2bProvisioningMessage}
                </Typography>
              )}
              {companyProvisioningPresentation.rolloutGuidance && (
                <Typography variant="body2">
                  {companyProvisioningPresentation.rolloutGuidance}
                </Typography>
              )}
              <Link component={RouterLink} to={companyAdminPath}>
                Open company settings
              </Link>
              .
            </Alert>
          )}
          {b2bRolloutError && (
            <Alert className="mt-3" severity="error">
              {b2bRolloutError}
            </Alert>
          )}
        </div>

        <div className="mt-2 border-t border-cement-300 pt-4">
          <Typography className="mb-2 text-xs font-semibold uppercase text-gray-600">
            Roles
          </Typography>

          {isRolesLoading && (
            <Typography className="text-gray-500" variant="body2">
              Loading roles...
            </Typography>
          )}

          {isRolesError && (
            <Typography className="text-red-600" variant="body2">
              Unable to load roles.
            </Typography>
          )}

          {!isRolesLoading &&
            !isRolesError &&
            (visibleRoles.length === 0 ? (
              <Typography className="text-gray-500" variant="body2">
                No roles assigned.
              </Typography>
            ) : (
              <ul className="space-y-1">
                {visibleRoles.map((role) => (
                  <li
                    key={`${role.name}-${role.resourceType || 'global'}-${
                      role.resourceId || 'none'
                    }`}
                    className="flex items-center justify-between text-sm"
                  >
                    <span>{formatRoleLabel(role)}</span>
                    {canEditRole(role) && (
                      <Button
                        color="error"
                        disabled={isRemovingRole}
                        size="small"
                        variant="text"
                        onClick={() => setRoleToRemove(role)}
                      >
                        Remove
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            ))}

          {rolesError && (
            <Typography className="mt-2 text-red-600" variant="body2">
              {rolesError}
            </Typography>
          )}

          <div className="mt-3 space-y-2">
            <div className="flex flex-col gap-2 sm:flex-row">
              <label className="flex-1 text-xs" htmlFor="role-select">
                Role
                <select
                  className="mt-1 w-full rounded border border-cement-300 p-1 text-sm"
                  id="role-select"
                  value={selectedRoleName}
                  onChange={(event) =>
                    setSelectedRoleName(
                      event.target.value as 'support' | 'admin',
                    )
                  }
                >
                  <option value="support">support</option>
                  <option value="admin">admin</option>
                </select>
              </label>
            </div>

            <Button
              className="w-full sm:w-auto"
              disabled={isAddingRole}
              loading={isAddingRole}
              variant="outlined"
              onClick={onAddRole}
            >
              Add role
            </Button>
          </div>
        </div>
      </div>

      <Dialog open={showDeactivate}>
        <Typography className="px-4 py-2" variant="h5">
          Deactivate account
        </Typography>
        <Typography className="px-4 py-2 text-gray-500">
          Are you sure you want to deactivate this account? All coaches will be
          removed and user will be deactivated. Any subscriptions will also be
          canceled and money time will be prorated back.
        </Typography>
        <DialogActions className="mt-2">
          <Button
            loading={processing}
            variant="outlined"
            onClick={() => setShowDeactivate(false)}
          >
            Cancel
          </Button>
          <Button
            className="ml-3"
            color="warning"
            loading={processing}
            variant="contained"
            onClick={onDeactivate}
          >
            Deactivate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={roleToRemove !== null}>
        <Typography className="px-4 py-2" variant="h5">
          Remove role
        </Typography>
        <Typography className="px-4 py-2 text-gray-500">
          {roleToRemove
            ? `Are you sure you want to remove the "${formatRoleLabel(
                roleToRemove,
              )}" role from this member?`
            : ''}
        </Typography>
        <DialogActions className="mt-2">
          <Button
            disabled={isRemovingRole}
            variant="outlined"
            onClick={onCancelRemoveRole}
          >
            Cancel
          </Button>
          <Button
            className="ml-3"
            color="warning"
            loading={isRemovingRole}
            variant="contained"
            onClick={onConfirmRemoveRole}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showAdminConfirm}>
        <Typography className="px-4 py-2" variant="h5">
          Add admin role - Confirm
        </Typography>
        <Typography className="px-4 py-2 text-gray-500">
          You are about to grant <strong>full admin access</strong> to{' '}
          <strong>{client.fullName}</strong>.
        </Typography>
        <Typography className="px-4 py-2 text-gray-500">
          This user will be able to:
        </Typography>
        <ul className="list-disc px-8 py-2 text-sm text-gray-500">
          <li>View and manage all users</li>
          <li>View and manage all companies</li>
          <li>Assign and remove roles (including admin)</li>
          <li>Access all system features and data</li>
        </ul>
        <Typography className="px-4 py-2 font-semibold text-red-600">
          Are you absolutely sure you want to proceed?
        </Typography>
        <DialogActions className="mt-2">
          <Button
            disabled={isAddingRole}
            variant="outlined"
            onClick={() => setShowAdminConfirm(false)}
          >
            Cancel
          </Button>
          <Button
            className="ml-3"
            color="error"
            loading={isAddingRole}
            variant="contained"
            onClick={performAddRole}
          >
            Yes, Grant Admin Access
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default AdminOnly;
